/**
 * User Registration Page
 *
 * Implements the signup flow using Supabase Auth with email/password registration.
 * Uses react-hook-form for form management and Zod for validation.
 *
 * Features:
 * - Email validation with real-time feedback
 * - Password strength requirements
 * - Confirmation password matching
 * - Clear error messages for registration failures
 * - Loading state during signup
 * - Auto-redirect to login or dashboard on success
 *
 * See: docs/v2/03-auth-and-security.md
 *
 * ## E2E test additions / known gaps (GitHub issue #494 — "E2E:
 * Authentication — login, signup, OTP, password reset, session
 * management")
 *
 * Additive, low-risk changes made so this page's real behavior is
 * assertable from `e2e/signup-auth-flow.spec.ts`:
 * - `sonner` `toast.error(...)` on a failed `signUpWithEmail()` call
 *   (e.g. the "duplicate email" 409-equivalent Supabase returns), same
 *   pattern as `Login.tsx`.
 * - Password-visibility toggles on both password fields.
 * - Terms / Privacy Policy links (routes already exist:
 *   `/terms` / `/privacy`, see `app/router.tsx`) — this form had no links
 *   to them at all before.
 * - The "Hero Hire AI" heading is now a `<Link to="/">` (see `Login.tsx`'s
 *   identical change for rationale).
 *
 * TODO(not implemented — out of scope for an E2E-test-writing change):
 * the ticket describes a fully different signup shape than what exists
 * here, and none of the following is added by this change (documented
 * instead of being invented from scratch, per this run's "make a
 * best-effort attempt and add a TODO" rule):
 * - Separate "first name" / "last name" fields — this form has always had
 *   a single optional `fullName` field, not two required name fields.
 * - Any OTP / 6-digit-code verification step. There is no
 *   `/verify-code` route, no OTP modal, and `signUpWithEmail()` /
 *   Supabase's `auth.signUp()` here relies on Supabase's own email
 *   confirmation LINK flow (see the `onSubmit` branching below on
 *   `user.confirmation_sent_at`), not a numeric-code `verifyOtp()` flow.
 *   Building a parallel OTP-code UI on top of an email-confirmation-link
 *   flow would be a significant, undocumented product/architecture
 *   decision (which verification mechanism does this app actually use?)
 *   that a test-writing change shouldn't make unilaterally.
 * - Redirect to `/onboarding/welcome` on success — no `/onboarding/*`
 *   route exists in `app/router.tsx`; this still redirects to `/dashboard`
 *   or `/login` per the existing `confirmation_sent_at` branch.
 * See `e2e/signup-auth-flow.spec.ts`'s doc-comment for exactly which
 * ticket checkboxes are covered vs. explicitly skipped for these reasons.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { signUpWithEmail } from '@/lib/auth/supabaseAuth';

/**
 * Validation schema for signup form
 */
const SignupSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /[A-Z]/,
        'Password must contain at least one uppercase letter'
      )
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    fullName: z.string().min(1, 'Full name is required').optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof SignupSchema>;

export default function SignupPage() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(SignupSchema),
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    setApiError(null);

    const { user, error } = await signUpWithEmail(data.email, data.password, {
      full_name: data.fullName || '',
    });

    if (error) {
      setApiError(error.message);
      toast.error(error.message);
      setIsSubmitting(false);
      return;
    }

    // Show success message
    setSignupSuccess(true);

    // If email confirmation is not required, redirect immediately
    if (user && !user.confirmation_sent_at) {
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } else {
      // Otherwise, redirect to login after a brief delay
      setTimeout(() => {
        navigate('/login', {
          state: {
            message:
              'Check your email to confirm your account before signing in.',
          },
        });
      }, 2000);
    }
  };

  if (signupSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-green-600">
              Welcome to Hero Hire AI!
            </h1>
            <p className="text-muted-foreground mt-4">
              Your account has been created successfully.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Check your email to confirm your account.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Redirecting to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
        <div className="text-center">
          <Link to="/" data-testid="signup-logo">
            <h1 className="text-3xl font-bold">Hero Hire AI</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Create your account</p>
        </div>

        {apiError && (
          <div className="p-4 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium mb-2">
              Full Name (optional)
            </label>
            <input
              id="fullName"
              type="text"
              placeholder="John Doe"
              {...register('fullName')}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-destructive">
                {errors.fullName.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('password')}
                className="w-full px-3 py-2 pr-10 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-password-visibility"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-destructive">
                {errors.password.message}
              </p>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Must be at least 8 characters with uppercase, lowercase, and
              number
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium mb-2"
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('confirmPassword')}
                className="w-full px-3 py-2 pr-10 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-confirm-password-visibility"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-destructive">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>

          {/* Terms / Privacy links */}
          <p className="text-center text-xs text-muted-foreground">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline font-medium">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-primary hover:underline font-medium">
              Privacy Policy
            </Link>
            .
          </p>
        </form>

        {/* Sign In Link */}
        <div className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline font-medium">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
